import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * 🚀 Smart API Cache Hook
 * Minskar API-anrop med 70-90% genom intelligent caching
 * 
 * @param {string} key - Unik cache-nyckel
 * @param {Function} fetchFn - Funktion som hämtar data
 * @param {number} ttl - Cache TTL i millisekunder (default: 5 min)
 * @param {Array} dependencies - Dependencies som triggar refetch
 * @param {*} defaultValue - Default värde (använd [] för arrays)
 * @param {boolean} skipInitialFetch - Om true, hoppar över initial fetch till dependencies är truthy
 */
const useApiCache = (key, fetchFn, ttl = 300000, dependencies = [], defaultValue = null, skipInitialFetch = false) => {
  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cache lagras i sessionStorage för att överleva komponenten men inte webbläsarsessionen
  const cacheRef = useRef(null);
  
  // Lagra fetchFn i ref för att undvika re-renders
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;
  
  // Ref för fetchData funktion för global cache invalidation
  const fetchDataRef = useRef();
  
  // 🚀 OPTIMERING: Global cache invalidation system
  useEffect(() => {
    const handleCacheInvalidation = (event) => {
      const { cacheKey } = event.detail;
      if (cacheKey === key || (Array.isArray(cacheKey) && cacheKey.includes(key))) {
        // Clear cache
        cacheRef.current = null;
        sessionStorage.removeItem(`api_cache_${key}`);
        setData(defaultValue);
        setLoading(true);
        // Fetch fresh data immediately via ref to avoid circular dependencies
        fetchDataRef.current(true);
      }
    };

    window.addEventListener('invalidateCache', handleCacheInvalidation);
    return () => {
      window.removeEventListener('invalidateCache', handleCacheInvalidation);
    };
  }, [key, defaultValue]);

  // Initialisera cache från sessionStorage
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(`api_cache_${key}`);
      if (cached) {
        const { data: cachedData, timestamp } = JSON.parse(cached);
        const now = Date.now();
        
        if ((now - timestamp) < ttl) {
          cacheRef.current = { data: cachedData, timestamp, hasLoggedHit: true };
          setData(cachedData);
          setLoading(false);
          return;
        } else {
          sessionStorage.removeItem(`api_cache_${key}`);
        }
      }
    } catch (e) {
      console.warn(`Cache fel för ${key}:`, e);
    }
  }, [key, ttl]);

  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      // Kontrollera cache först (om inte forced refresh)
      if (!forceRefresh && cacheRef.current) {
        const now = Date.now();
        const age = now - cacheRef.current.timestamp;
        
        if (age < ttl) {
          setData(cacheRef.current.data);
          setLoading(false);
          return cacheRef.current.data;
        }
      }

      setLoading(true);
      setError(null);
      
      const result = await fetchFnRef.current();
      const timestamp = Date.now();
      
      // Spara i både memory och sessionStorage
      cacheRef.current = { data: result, timestamp, hasLoggedHit: false };
      
      try {
        sessionStorage.setItem(`api_cache_${key}`, JSON.stringify({
          data: result,
          timestamp
        }));
      } catch (e) {
        console.warn(`Kunde inte spara cache för ${key}:`, e);
      }
      
      setData(result);
      setLoading(false);
      return result;
    } catch (err) {
      console.error(`API fel för ${key}:`, err);
      setError(err);
      setLoading(false);
      throw err;
    }
  }, [ttl, key]); // Ta bort fetchFn från dependencies för att förhindra infinite loops

  // Auto-fetch när hook först mountas och vid dependencies ändringar
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // 🚀 OPTIMERING: Hoppa över initial fetch om skipInitialFetch är true
      // och dependencies innehåller falsy värden
      if (skipInitialFetch && dependencies.length > 0) {
        const hasTruthyDep = dependencies.some(dep => Boolean(dep));
        if (!hasTruthyDep) {
          setLoading(false);
          return;
        }
      }
      
      fetchData();
    }
    // fetchData och dependencies utelämnade med avsikt: detta är den INITIALA
    // hämtningen, keyad på cache-nyckeln. Att ta med dem hade gett en hämtning
    // per rendering. Ändrade dependencies hanteras av effekten nedanför.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, skipInitialFetch]); // Lägg till skipInitialFetch i dependencies

    // Separat effect för dependency changes som tvingar refetch
  const prevDeps = useRef([]);
  const isFirstDepsCheck = useRef(true);
  
  useEffect(() => {
    if (hasInitialized.current && dependencies.length > 0) {
      // Hoppa över första dependency check för att undvika dubbel fetch
      if (isFirstDepsCheck.current) {
        isFirstDepsCheck.current = false;
        prevDeps.current = [...dependencies];
        return;
      }
      
      // Jämför faktiska värden, inte array-referenser
      const depsChanged = dependencies.some((dep, i) => dep !== prevDeps.current[i]) || 
                          dependencies.length !== prevDeps.current.length;
      
      if (depsChanged) {
        prevDeps.current = [...dependencies];
        fetchData(true);
       }
    }
    // dependencies skickas vidare som beroendelista rakt av. ESLint kan inte
    // verifiera en lista som inte är ett array-literal — men det är hela
    // poängen: hooken jämför värdena själv mot prevDeps ovan, i stället för
    // att lita på array-referenser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // Cleanup function för cache invalidation
  const invalidateCache = useCallback(() => {
    cacheRef.current = null;
    sessionStorage.removeItem(`api_cache_${key}`);
    setData(defaultValue);
    setLoading(true);
  }, [key, defaultValue]);

  // Force refresh function
  const refresh = useCallback(() => {
    return fetchData(true);
    // fetchData utelämnad med avsikt: refresh ska vara stabil för
    // konsumenterna. fetchData byts bara när ttl eller key ändras.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]); // Bara beroende av key, inte fetchData

  // Update fetchDataRef when fetchData changes
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchData, 
    refresh,
    invalidateCache,
    isCached: !loading && cacheRef.current !== null
  };
};

// 🚀 OPTIMERING: Global function för att invalidera cache från andra komponenter
// Stödjer partial matching - 'myassigned_projects' invaliderar alla 'myassigned_projects_*'
export const invalidateGlobalCache = (cacheKeys) => {
  const keys = Array.isArray(cacheKeys) ? cacheKeys : [cacheKeys];

  // Invalidera sessionStorage cache med partial matching
  const allSessionKeys = Object.keys(sessionStorage);
  keys.forEach(keyPattern => {
    // Hitta alla cache-nycklar som matchar mönstret
    allSessionKeys.forEach(sessionKey => {
      if (sessionKey.startsWith(`api_cache_${keyPattern}`)) {
        sessionStorage.removeItem(sessionKey);
      }
    });
  });

  // Trigger event för att uppdatera komponenter
  window.dispatchEvent(new CustomEvent('invalidateCache', {
    detail: { cacheKey: keys }
  }));

};

export default useApiCache; 